import { doc, getDoc, collection, getDocs, addDoc, setDoc, updateDoc, query, where } from 'firebase/firestore';
import { db } from '../../data/base';
import dayjs from 'dayjs';
import { secureGetItem } from '../../data/utils';

/**
 * Fetches available licenses for the current user
 * For admins: fetches all active subscriptions with valid endDate
 * For regular users: fetches licenses associated with their client
 * @returns {Promise<Array>} Array of license objects with catalog limit info
 */
/**
 * Adds a product and its selected variants to a catalog
 * Clones the product from Products to CataloguesProducts
 * Clones the variants from Variants to CataloguesVariants
 * Updates references between the cloned entities
 * 
 * @param {string} productId - ID of the original product
 * @param {Array<string>} variantIds - Array of variant IDs to clone
 * @param {string} catalogId - ID of the catalog to add the product to
 * @returns {Promise<Object>} Object containing the new product ID and variant IDs
 */
export const addProductToCatalog = async (productId, variantIds, catalogId) => {

    try {
        // 1. Fetch the original product
        const productRef = doc(db, 'Products', productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            throw new Error(`Product with ID ${productId} not found`);
        }

        const productData = productSnap.data();

        // 2. Check if a CatalogueProduct with the same mainProduct already exists in this catalog
        const existingProductQuery = query(
            collection(db, 'CataloguesProducts'),
            where('mainProduct', '==', productId),
            where('catalogRef', '==', catalogId)
        );
        const existingProductSnapshot = await getDocs(existingProductQuery);

        let existingCatalogueProduct = null;
        if (!existingProductSnapshot.empty) {
            // Found existing catalogue product
            existingCatalogueProduct = {
                id: existingProductSnapshot.docs[0].id,
                data: existingProductSnapshot.docs[0].data()
            };
        }

        // 3. Fetch all selected variants
        const variantDataMap = {};
        for (const variantId of variantIds) {
            const variantRef = doc(db, 'Variants', variantId);
            const variantSnap = await getDoc(variantRef);

            if (!variantSnap.exists()) {
                console.warn(`Variant with ID ${variantId} not found, skipping`);
                continue;
            }

            variantDataMap[variantId] = variantSnap.data();
        }

        // 4. Check for existing variants in this specific catalog to avoid duplicates
        let existingVariantMainRefs = new Set();

        if (existingCatalogueProduct) {
            // Se il prodotto esiste già, controlla quali varianti sono già presenti in questo catalogo
            const existingVariantsQuery = query(
                collection(db, 'CataloguesVariants'),
                where('catalogProductRef', '==', existingCatalogueProduct.id),
                where('mainVariantRef', 'in', variantIds)
            );
            const existingVariantsSnapshot = await getDocs(existingVariantsQuery);

            existingVariantsSnapshot.forEach(doc => {
                const data = doc.data();
                if (data.mainVariantRef) {
                    existingVariantMainRefs.add(data.mainVariantRef);
                }
            });
        }

        // Filter out variants that already exist in this specific catalog
        const variantsToAdd = variantIds.filter(variantId => !existingVariantMainRefs.has(variantId));

        if (variantsToAdd.length === 0) {
            throw new Error('All selected variants are already present in this catalogue');
        }

        // 5. Clone variants to CataloguesVariants
        const newVariantIds = [];
        const variantIdMapping = {}; // Maps original variant IDs to new variant IDs

        // Determine the catalogProductRef to use
        const catalogProductRefToUse = existingCatalogueProduct ? existingCatalogueProduct.id : null;

        for (const variantId of variantsToAdd) {
            if (!variantDataMap[variantId]) continue;

            // Clone the variant data and add required fields, excluding the original 'id' field
            const { id: originalId, ...variantDataWithoutId } = variantDataMap[variantId];
            const variantData = {
                ...variantDataWithoutId,
                isVisible: true,
                mainVariantRef: variantId,
                // catalogProductRef will be set after we determine the final product ID
                catalogProductRef: catalogProductRefToUse
            };

            // Add to CataloguesVariants
            const newVariantRef = await addDoc(collection(db, 'CataloguesVariants'), variantData);
            const newVariantId = newVariantRef.id;

            // Update the document with the new ID
            await updateDoc(newVariantRef, {
                id: newVariantId
            });

            newVariantIds.push(newVariantId);
            variantIdMapping[variantId] = newVariantId;
        }

        let finalProductId;

        // 🎯 CREA LA MAPPA DELLE VARIANTI per le nuove varianti aggiunte
        const newVariantsMap = variantsToAdd.map(originalVariantId => {
            const newVariantId = variantIdMapping[originalVariantId];
            const originalVariantData = variantDataMap[originalVariantId];

            return {
                id: newVariantId, // ID della variante clonata
                skuCode: originalVariantData.skuModel || '',
                eanCode: originalVariantData.eanCode || '',
                upcCode: originalVariantData.upcCode || '',
                variantName: originalVariantData.glassesName || ''
            };
        });

        if (existingCatalogueProduct) {
            // 5a. Product already exists - update list_variants with new variants
            finalProductId = existingCatalogueProduct.id;
            const currentVariants = existingCatalogueProduct.data.list_variants || [];
            const updatedVariants = [...currentVariants, ...newVariantIds];

            // 🎯 AGGIORNA LA MAPPA ESISTENTE aggiungendo le nuove varianti
            const currentMap = existingCatalogueProduct.data.catalogues_variants_map || [];
            const updatedMap = [...currentMap, ...newVariantsMap];

            await updateDoc(doc(db, 'CataloguesProducts', finalProductId), {
                list_variants: updatedVariants,
                catalogues_variants_map: updatedMap
            });

            // Update catalogProductRef in the new variants (if not set above)
            if (!catalogProductRefToUse) {
                for (const newVariantId of newVariantIds) {
                    await updateDoc(doc(db, 'CataloguesVariants', newVariantId), {
                        catalogProductRef: finalProductId
                    });
                }
            }
        } else {
            // 5b. Product doesn't exist - create new CatalogueProduct
            // Clone the product data excluding the original 'id' field
            const { id: originalProductId, ...productDataWithoutId } = productData;
            const newProductData = {
                ...productDataWithoutId,
                isVisible: true,
                list_variants: newVariantIds, // Replace with new variant IDs
                catalogues_variants_map: newVariantsMap, // 🎯 CREA LA MAPPA DELLE VARIANTI
                mainProduct: productId,
                catalogRef: catalogId
            };

            // Add to CataloguesProducts
            const newProductRef = await addDoc(collection(db, 'CataloguesProducts'), newProductData);
            finalProductId = newProductRef.id;

            // Update the product document with the new ID
            await updateDoc(newProductRef, {
                id: finalProductId
            });

            // Update catalogProductRef in all cloned variants
            for (const newVariantId of newVariantIds) {
                await updateDoc(doc(db, 'CataloguesVariants', newVariantId), {
                    catalogProductRef: finalProductId
                });
            }
        }

        // 6. Update catalog SKU count in ARShadesSubscriptions if needed
        // This step is optional and depends on your requirements

        return {
            newProductId: finalProductId,
            newVariantIds,
            originalProductId: productId,
            originalVariantIds: variantIds,
            addedVariantIds: variantsToAdd, // IDs delle varianti effettivamente aggiunte
            skippedVariantIds: variantIds.filter(id => !variantsToAdd.includes(id)), // IDs delle varianti saltate
            catalogId,
            existingProduct: !!existingCatalogueProduct // Indica se il prodotto esisteva già
        };
    } catch (error) {
        console.error('Error adding product to catalog:', error);
        throw error;
    }
};

/**
 * Fetches available licenses for the current user
 * For admins: fetches all active subscriptions with valid endDate
 * For regular users: fetches licenses associated with their client
 * @returns {Promise<Array>} Array of license objects with catalog limit info
 */
export const fetchAvailableLicenses = async () => {
    try {
        // 1. Get user email
        let email = null;
        try {
            email = secureGetItem('email');
        } catch (e) { }

        if (!email) {
            throw new Error('User email not found.');
        }

        email = email.toLowerCase();

        // 2. Get clientRef from Profile
        const profileRef = doc(db, 'Profiles', email);
        const profileSnap = await getDoc(profileRef);

        if (!profileSnap.exists()) {
            throw new Error('User profile not found.');
        }

        const profileData = profileSnap.data();
        const userRole = profileData.role || profileData.userRole;
        const clientRef = profileData.clientRef; // 🔥 AGGIUNTO: Recupera clientRef come in DataConsumption

        // Admin path: fetch all valid subscriptions
        if (userRole && userRole.toLowerCase() === 'admin') {
            const now = dayjs();
            const subsQuery = query(
                collection(db, 'ARShadesSubscriptions'),
                where("status", "==", "Active") // 🔥 AGGIUNTO: Filtra per status come in DataConsumption
            );
            const subsSnap = await getDocs(subsQuery);
            const allLicenses = [];

            subsSnap.forEach(docSnap => {
                const data = docSnap.data();
                if (!data.endDate) return;

                let endDate = data.endDate;
                // Firestore Timestamp or JS Date
                if (endDate.seconds) endDate = dayjs.unix(endDate.seconds);
                else endDate = dayjs(endDate);

                if (endDate.isAfter(now)) { // 🔥 RIMOSSO: && data.status === 'Active' (già filtrato nella query)
                    // Extract catalog limits and used
                    const listCatalogues = data.list_catalogues || {};
                    const catalogsLimit = listCatalogues.catalogLimit ?? 0;
                    const skuLimit = listCatalogues.skuLimit ?? 0;
                    const catalogRefList = Array.isArray(listCatalogues.list_catalogues_refs) ? listCatalogues.list_catalogues_refs : [];

                    allLicenses.push({
                        id: docSnap.id,
                        name: data.name || docSnap.id,
                        catalogsLimit,
                        skuLimit,
                        catalogRefList,
                        catalogsAvailable: catalogsLimit === 0 ? "No Limits" : catalogsLimit - catalogRefList.length,
                        subscriptionType: data.subscriptionType || null,
                        webUrl: data.webUrl || null
                    });
                }
            });

            return allLicenses;
        }

        // 🔥 AGGIUNTO: Recupera i cataloghi del profilo utente
        const userCatalogsList = profileData.list_catalogues || [];

        // Se l'utente non ha cataloghi nel profilo, non può vedere nessuna subscription
        if (userCatalogsList.length === 0) {
            console.warn('User has no catalogues in profile, returning empty licenses list');
            return [];
        }

        // 🔥 MODIFICATO: Regular user path con query allineata a DataConsumption
        const now = dayjs();

        // 🔥 IMPORTANTE: Filtriamo per clientRef E status come in DataConsumption
        const subsQuery = query(
            collection(db, 'ARShadesSubscriptions'),
            where("clientRef", "==", clientRef),
            where("status", "==", "Active")
        );
        const subsSnap = await getDocs(subsQuery);
        const userLicenses = [];

        subsSnap.forEach(docSnap => {
            const data = docSnap.data();

            // Controlla solo se non è scaduta (status già filtrato nella query)
            if (data.endDate) {
                let endDate = data.endDate;
                // Firestore Timestamp or JS Date
                if (endDate.seconds) endDate = dayjs.unix(endDate.seconds);
                else endDate = dayjs(endDate);

                // Se è scaduta, skip
                if (!endDate.isAfter(now)) return;
            }

            // Extract catalog limits and used
            const listCatalogues = data.list_catalogues || {};
            const catalogsLimit = listCatalogues.catalogLimit ?? 0;
            const skuLimit = listCatalogues.skuLimit ?? 0;
            const catalogRefList = Array.isArray(listCatalogues.list_catalogues_refs) ? listCatalogues.list_catalogues_refs : [];

            // 🔥 CONTROLLO CATALOGHI: Verifica se la subscription ha cataloghi in comune con il profilo utente
            const commonCatalogues = catalogRefList.filter(catalogId => userCatalogsList.includes(catalogId));

            // Solo se ci sono cataloghi in comune, aggiungi la subscription
            if (commonCatalogues.length > 0) {

                userLicenses.push({
                    id: docSnap.id,
                    name: data.name || docSnap.id,
                    catalogsLimit,
                    skuLimit,
                    catalogRefList: commonCatalogues, // 🔥 Mostra solo i cataloghi che l'utente può effettivamente utilizzare
                    catalogsAvailable: catalogsLimit === 0 ? "No Limits" : catalogsLimit - catalogRefList.length,
                    subscriptionType: data.subscriptionType || null,
                    webUrl: data.webUrl || null
                });
            }
        });
        return userLicenses;
    } catch (error) {
        console.error('Error fetching licenses:', error);
        throw error;
    }
};

export const fetchCatalogSkuLimits = async (catalogIds, availableLicenses, setLoadingSkuLimits) => {
    if (!catalogIds || catalogIds.length === 0) return {};

    try {
        setLoadingSkuLimits(true);

        // Usa i dati delle licenze già salvati in Redux invece di fare una nuova chiamata a Firestore
        if (!availableLicenses || availableLicenses.length === 0) {
            return {};
        }

        // Mappa per tenere traccia di quale licenza contiene quale catalogo
        const catalogToLicenseMap = {};

        // Per ogni licenza, controlla quali cataloghi contiene
        availableLicenses.forEach(license => {
            const catalogRefList = license.catalogRefList || [];

            // Per ogni catalogo nella licenza, memorizza la licenza associata
            catalogRefList.forEach(catalogId => {
                catalogToLicenseMap[catalogId] = license;
            });
        });

        // Risultato finale che conterrà i limiti per ogni catalogo
        const catalogLimits = {};

        // Per ogni catalogo richiesto
        for (const catalogId of catalogIds) {
            // Trova la licenza associata a questo catalogo
            const license = catalogToLicenseMap[catalogId];

            if (!license) {
                // Catalogo non associato a nessuna licenza, ma contiamo comunque le varianti
                // Trova tutti i CataloguesProducts per questo catalogo
                const catalogProductsRef = collection(db, 'CataloguesProducts');
                const catalogProductsQuery = query(
                    catalogProductsRef,
                    where('catalogRef', '==', catalogId)
                );
                const catalogProductsSnapshot = await getDocs(catalogProductsQuery);

                // Conta il numero totale di varianti sommando le lunghezze dei list_variants
                let totalVariants = 0;

                catalogProductsSnapshot.forEach(doc => {
                    const data = doc.data();
                    const listVariants = data.list_variants || [];
                    totalVariants += listVariants.length;
                });

                catalogLimits[catalogId] = {
                    skuLimit: 0,
                    currentSkuCount: totalVariants,
                    isUnlimited: true
                };
                continue;
            }

            // Ottieni il limite SKU dalla licenza
            const skuLimit = license.skuLimit || 0;

            // Se skuLimit è 0, significa che è illimitato
            if (skuLimit === 0) {
                catalogLimits[catalogId] = {
                    skuLimit: 0,
                    currentSkuCount: 0,
                    isUnlimited: true
                };
                continue;
            }

            // Trova tutti i CataloguesProducts per questo catalogo
            const catalogProductsRef = collection(db, 'CataloguesProducts');
            const catalogProductsQuery = query(
                catalogProductsRef,
                where('catalogRef', '==', catalogId)
            );
            const catalogProductsSnapshot = await getDocs(catalogProductsQuery);

            // Se non ci sono prodotti, non ci sono nemmeno varianti
            if (catalogProductsSnapshot.empty) {
                catalogLimits[catalogId] = {
                    skuLimit: skuLimit,
                    currentSkuCount: 0,
                    isUnlimited: skuLimit === 0
                };
                continue;
            }

            // Conta il numero totale di varianti sommando le lunghezze dei list_variants
            let totalVariants = 0;
            const variantsDetails = [];

            catalogProductsSnapshot.forEach(doc => {
                const data = doc.data();
                const listVariants = data.list_variants || [];

                variantsDetails.push({
                    productId: doc.id,
                    productName: data.name || 'Unnamed',
                    variantsCount: listVariants.length
                });

                totalVariants += listVariants.length;
            });

            catalogLimits[catalogId] = {
                skuLimit: skuLimit,
                currentSkuCount: totalVariants,
                isUnlimited: skuLimit === 0
            };
        }

        return catalogLimits;

    } catch (error) {
        console.error('Error fetching catalog SKU limits:', error);
        return {};
    } finally {
        setLoadingSkuLimits(false);
    }
};

export const fetchCatalogs = async (setLoadingCatalogs, setUserCatalogues, message, setCatalogsData, setCatalogNames, setVariantCatalogues, setCatalogSkuLimits, selectedProduct, variants, availableLicenses, findVariantCatalogues) => {
    setLoadingCatalogs(true);
    try {
        // Recupera la lista dei cataloghi dal secure storage
        const userCatalogsList = secureGetItem('p_list_catalogues') || [];
        setUserCatalogues(userCatalogsList);

        if (userCatalogsList.length === 0) {
            setLoadingCatalogs(false);
            return;
        }

        // Recupera i dettagli dei cataloghi da Firestore
        const catalogsPromises = userCatalogsList.map(async (catalogId) => {
            try {
                const docRef = doc(db, 'Catalogues', catalogId);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    return {
                        id: docSnap.id,
                        nameCatalog: data.nameCatalog || `Catalog ${docSnap.id.substring(0, 5)}...`
                    };
                } else {
                    return {
                        id: catalogId,
                        nameCatalog: `Catalog ${catalogId.substring(0, 5)}...`
                    };
                }
            } catch (error) {
                console.error(`Error fetching catalog ${catalogId}:`, error);
                return {
                    id: catalogId,
                    nameCatalog: `Catalog ${catalogId.substring(0, 5)}...`
                };
            }
        });

        const results = await Promise.all(catalogsPromises);
        const validCatalogs = results
            .filter(c => c !== null)
            .sort((a, b) => (a.nameCatalog || '').localeCompare(b.nameCatalog || ''));

        setCatalogsData(validCatalogs);

        // Crea una mappa di nomi cataloghi per ID
        const catalogNamesMap = {};
        validCatalogs.forEach(catalog => {
            catalogNamesMap[catalog.id] = catalog.nameCatalog;
        });
        setCatalogNames(catalogNamesMap);

        // Se abbiamo un prodotto selezionato e varianti, carica i cataloghi delle varianti
        if (selectedProduct && selectedProduct.id && variants[selectedProduct.id]) {
            const variantIds = variants[selectedProduct.id]
                .filter(variant => variant.status === 'Published')
                .map(variant => variant.id);

            const variantCataloguesResult = await findVariantCatalogues(selectedProduct.id, variantIds);
            setVariantCatalogues(variantCataloguesResult);
        }

        // Carica i limiti SKU per tutti i cataloghi solo se setCatalogSkuLimits è fornito
        if (setCatalogSkuLimits) {
            const catalogIds = validCatalogs.map(catalog => catalog.id);
            const skuLimitsResult = await fetchCatalogSkuLimits(catalogIds, availableLicenses, setCatalogSkuLimits);
            setCatalogSkuLimits(skuLimitsResult);
        }
    } catch (error) {
        console.error('Error loading catalogs:', error);
        message.error('Error loading catalogs');
    } finally {
        setLoadingCatalogs(false);
    }
};
